import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { RowActionOption } from '@/components/table-rest-api/types';
import type { ListActionPayload } from './types';

/**
 * Interface for action component refs
 */
interface ActionComponentRef {
  open?: (payload: ListActionPayload) => void;
}

/**
 * Options for useListsActions composable
 */
interface UseListsActionsOptions {
  renameListRef: Ref<ActionComponentRef | null>;
  duplicateListRef: Ref<ActionComponentRef | null>;
  deleteListRef: Ref<ActionComponentRef | null>;
  onOpenImportModal?: (listId: number) => void;
  onOpenSegmentationEditor?: (listId: number) => void;
}

/**
 * Composable that provides row actions for lists management
 * Actions are configured to open the corresponding action components
 */
export default function useListsActions(options: UseListsActionsOptions) {
  const { t } = useI18n();
  const router = useRouter();

  /**
   * Row actions - triggered from individual row menu
   * Uses rowData for accessing multiple properties (name, type, etc.)
   * Uses rowKey for the unique identifier
   */
  const rowActions = computed<RowActionOption[]>(() => [
    {
      label: t('myLists.manage.actions.clientManage'),
      icon: 'fa fa-users',
      command: (_rowData, rowKey) => {
        router.push(`/lists/manage-contacts/${rowKey}`);
      },
    },
    {
      label: t('myLists.manage.actions.handleListSegment'),
      icon: 'fa fa-filter',
      visible: (rowData) => rowData.type === 'segment',
      command: (rowData) => {
        if (options.onOpenSegmentationEditor) {
          options.onOpenSegmentationEditor(rowData.id_shop_list);
        }
      },
    },
    {
      label: t('myLists.manage.actions.clientImport'),
      icon: 'fa fa-file-import',
      visible: (rowData) => rowData.type !== 'segment',
      command: (rowData) => {
        if (options.onOpenImportModal) {
          options.onOpenImportModal(rowData.id_shop_list);
        }
      },
    },
    { separator: true, label: '', command: () => { /* separator */ } },
    {
      label: t('myLists.manage.actions.listRename.menuItem'),
      icon: 'fa fa-pencil',
      command: (rowData) => {
        if (options.renameListRef.value?.open) {
          options.renameListRef.value.open({
            listId: rowData.id_shop_list,
            listName: rowData.name,
          });
        }
      },
    },
    {
      label: t('myLists.manage.actions.clientClone'),
      icon: 'fa fa-copy',
      command: (rowData) => {
        if (options.duplicateListRef.value?.open) {
          options.duplicateListRef.value.open({
            listId: rowData.id_shop_list,
            listName: rowData.name,
          });
        }
      },
    },
    {
      label: t('myLists.manage.actions.listDelete.menuItem'),
      icon: 'fa fa-trash',
      class: 'p-button-danger',
      command: (rowData) => {
        if (options.deleteListRef.value?.open) {
          options.deleteListRef.value.open({
            listId: rowData.id_shop_list,
            listName: rowData.name,
          });
        }
      },
    },
  ]);

  return {
    rowActions,
  };
}
